/**
 * Corpus Statistics Service
 *
 * Provides real, data-backed statistics from the PE-certified corpus.
 * All values are derived from actual measurements, not estimates.
 *
 * CRITICAL UNDERSTANDING - HOW PERMAFROST NMR WORKS:
 * PermaFrost NMR is a chemical treatment that works consistently on metal surfaces.
 * The treatment ALWAYS restores close to 100% of cooling capacity lost to thermal
 * degradation in heat exchangers. The variance in improvement percentages reflects
 * BASELINE CONDITIONS, not treatment inconsistency:
 *
 * - Lower improvement (3-15%): Equipment had less thermal degradation
 *   (newer, well-maintained, recently serviced, pharma-grade maintenance)
 * - Higher improvement (25-50%+): Equipment had significant thermal degradation
 *   (older, deferred maintenance, high runtime hours)
 *
 * PREREQUISITES FOR ACCURATE MEASUREMENT:
 * Equipment must be operating correctly with:
 * - Correct refrigerant type and charge
 * - Proper airflow (clean filters, working fans/pulleys)
 * - No mechanical failures
 * If these are off, results are skewed by other variables (like measuring a car's
 * MPG with a broken wheel - the engine treatment works but can't be measured).
 *
 * The corpus excludes faulty equipment. UL Lab testing is the gold standard;
 * real-world data without variable interference should closely match lab results.
 *
 * DATA SOURCE COUNTS:
 * - 129 = efficiency MEASUREMENTS (case studies with efficiency data)
 * - 143 = total records
 * - 113 = PE-certified field studies
 * - 121 = kW/ton measurements
 * - 132 = financial measurements
 */

import {
  calculateAvoidedDegradationCosts,
  calculateAvoidedDemandCharges,
  calculateAvoidedExtendedRuntimeCosts,
  calculateLifeExtensionValue,
} from './degradationCurve';

// Overall efficiency improvement statistics (n=129 measurements from 143 case studies)
export const EFFICIENCY_STATS = {
  n: 129,              // Total efficiency measurements
  nCaseStudies: 143,   // Unique case studies
  mean: 29.83,
  median: 25.00,
  stdDev: 18.66,
  min: 4.1,
  max: 84.4,
  q1: 16.8,
  q3: 35.9,
  ci95: { lower: 26.62, upper: 33.04 },
  standardError: 1.64  // stdDev / sqrt(n) = 18.66 / sqrt(129)
}

// kW/ton statistics (n=121 measurements)
export const KW_TON_STATS = {
  n: 121,
  before: {
    mean: 1.235,
    stdDev: 0.57,
    median: 1.24,
    min: 0.1,
    max: 3.3
  },
  after: {
    mean: 0.889,
    stdDev: 0.42,
    median: 0.98,
    min: 0.10,
    max: 2.23
  },
  reduction: {
    meanPct: 28.0,
    stdDev: 18.7
  }
}

// COP statistics (n=75 measurements) - NOTE: Small sample, treat with caution
export const COP_STATS = {
  n: 75,
  mean: 4.3,
  median: 3.63,
  stdDev: 1.8,
  min: 2.01,
  max: 9.9,
  note: 'Small sample size (n=75). Max value 9.9 may be outlier. Use with appropriate uncertainty.'
}

// Equipment-specific efficiency statistics (from 143 case studies)
// These are REAL values from PE-certified measurements
export const EQUIPMENT_STATS = {
  'Chiller': {
    n: 65,
    mean: 31.6,
    stdDev: 21.7,
    min: 4.1,
    max: 84.4,
    typicalCapacity: '50-3,125 tons',
    commonRefrigerants: ['R-134A', 'R-123', 'R-22']
  },
  'RTU': {
    n: 14,
    mean: 26.6,
    stdDev: 15.2,
    min: 13.0,
    max: 72.9,
    typicalCapacity: '3-50 tons',
    commonRefrigerants: ['R-22', 'R-410A']
  },
  'CRAC': {
    n: 13,
    mean: 26.6,
    stdDev: 10.2,
    min: 6.8,
    max: 46.1,
    typicalCapacity: '5-30 tons',
    commonRefrigerants: ['R-22', 'R-410A', 'R-407C']
  },
  'DX System': {
    n: 4,
    mean: 45.4,
    stdDev: 10.2,
    min: 35.0,
    max: 58.7,
    typicalCapacity: '1-10 tons',
    commonRefrigerants: ['R-22', 'R-410A']
  },
  'Split System': {
    n: 13,
    mean: 25.6,
    stdDev: 14.4,
    min: 7.8,
    max: 46.1,
    typicalCapacity: '1-10 tons',
    commonRefrigerants: ['R-22', 'R-410A']
  },
  'Refrigeration': {
    n: 12,
    mean: 27.3,
    stdDev: 14.1,
    min: 5.7,
    max: 58.2,
    typicalCapacity: '5-100 tons',
    commonRefrigerants: ['R-22', 'R-404A']
  }
}

// Financial statistics
export const FINANCIAL_STATS = {
  savingsPerTon: {
    value: 120,      // $120/ton/year median
    stdDev: 85,      // High variance due to different equipment types/climates
    ci95: { lower: 45, upper: 195 },  // Wide CI reflects real-world variability
    n: 23,           // Limited sample size - treat as estimate
    note: 'Annual savings per ton of cooling capacity. High variance reflects equipment type, climate, and runtime differences.',
    warning: 'n=23 is underpowered. Use with appropriate uncertainty bounds.'
  },
  meanAnnualSavings: {
    value: 16580,
    stdDev: 25859,   // Very high variance due to system size differences
    n: 132,
    median: 6772,    // Median more representative than mean
    min: 150,
    max: 187815,
    note: 'Large variance due to different system sizes. Per-ton normalization recommended.'
  },
  treatmentCostPerTon: {
    value: 125,
    range: { min: 100, max: 150 },
    n: 23,
    source: 'Average treatment cost across documented installations with capacity data',
    note: 'Varies by equipment type, location, and service provider. Request site-specific quote.'
  }
}

// Lower performers - cases with <15% improvement
export const LOWER_PERFORMERS = [
  { id: 'USNS', efficiency: 3.8, reason: 'Marine application, recently overhauled' },
  { id: 'Major Global Bank A', efficiency: 4.1, reason: 'Well-maintained data center' },
  { id: 'YMCA Facility', efficiency: 4.1, reason: 'Recent refrigerant work' },
  { id: 'BMS Pharmaceutical NB', efficiency: 4.6, reason: 'Pharmaceutical-grade maintenance' },
  { id: 'BMS Pharmaceutical Wallingford', efficiency: 6.0, reason: 'Pharmaceutical-grade maintenance' },
  { id: 'Retail Chain B Refrigeration', efficiency: 6.5, reason: 'Commercial refrigeration profile' },
  { id: 'Caribbean Telecom', efficiency: 6.8, reason: 'Tropical climate operation' },
  { id: 'Hotel Chain NOLA', efficiency: 7.7, reason: 'New equipment (4 years old)' },
  { id: 'MCDP', efficiency: 7.8, reason: 'Unknown - requires investigation' },
  { id: 'Industrial Chemical Company A', efficiency: 9.1, reason: 'Industrial application' },
  { id: 'QTS Data Center', efficiency: 9.2, reason: 'Young equipment, data center maintenance' }
]

// Screening criteria - when NOT to treat
export const SCREENING_CRITERIA = {
  minimumAgeYears: 3,
  avoidIf: [
    'Recent refrigerant service (within 6 months)',
    'New compressor installation',
    'Mechanical failures present',
    'Scheduled for replacement within 1 year',
    'Severe refrigerant leak'
  ],
  bestCandidates: [
    'Equipment 5-15 years old',
    'No recent major service',
    'Visible performance degradation',
    'High runtime hours'
  ],
  expectedLowerResults: [
    'New equipment (<3 years): expect 5-15% improvement',
    'Well-maintained systems: expect 10-20% improvement',
    'Pharmaceutical/data center facilities: expect 15-20% improvement'
  ]
}

// Data quality metrics
export const DATA_QUALITY = {
  peCertified: 113,
  totalCaseStudies: 143,
  ipmvpCompliant: true,
  thirdPartyValidation: {
    labs: 8,
    universities: 2,
    governmentFacilities: 5
  },
  classImbalance: {
    r22Refrigerant: 75, // percent
    rtuEquipment: 43,   // percent
    nyFlGeographic: 39  // percent
  }
}

/**
 * Get efficiency statistics for a specific equipment type
 */
export function getEquipmentStats(equipmentType) {
  const normalized = equipmentType?.replace(/[\s_-]+/g, ' ').toLowerCase()

  for (const [key, stats] of Object.entries(EQUIPMENT_STATS)) {
    if (key.toLowerCase() === normalized ||
        key.toLowerCase().includes(normalized) ||
        normalized?.includes(key.toLowerCase())) {
      return { type: key, ...stats }
    }
  }

  // Return overall stats if no match
  return {
    type: 'All Equipment',
    n: EFFICIENCY_STATS.n,
    mean: EFFICIENCY_STATS.mean,
    stdDev: EFFICIENCY_STATS.stdDev,
    min: EFFICIENCY_STATS.min,
    max: EFFICIENCY_STATS.max
  }
}

/**
 * Calculate expected efficiency improvement with uncertainty
 * @param {string} equipmentType - Equipment type
 * @param {number} equipmentAge - Age in years
 * @returns {Object} Expected improvement with bounds
 */
export function calculateExpectedImprovement(equipmentType, equipmentAge = 10) {
  const stats = getEquipmentStats(equipmentType)

  let expected = stats.mean

  // Adjust for equipment age
  if (equipmentAge < 3) {
    expected = Math.max(stats.min, stats.mean * 0.5) // New equipment: lower improvement
  } else if (equipmentAge < 5) {
    expected = stats.mean * 0.75
  } else if (equipmentAge > 15) {
    expected = Math.min(stats.max * 0.8, stats.mean * 1.15) // Older equipment: higher improvement
  }

  // Calculate confidence interval
  const se = stats.stdDev / Math.sqrt(stats.n)
  const ci95Lower = Math.max(stats.min, expected - 1.96 * se)
  const ci95Upper = Math.min(stats.max, expected + 1.96 * se)

  return {
    expected: expected.toFixed(1),
    stdDev: stats.stdDev.toFixed(1),
    floor: Math.max(stats.min, expected - stats.stdDev).toFixed(1),
    optimistic: Math.min(stats.max, expected + stats.stdDev).toFixed(1),
    ci95: {
      lower: ci95Lower.toFixed(1),
      upper: ci95Upper.toFixed(1)
    },
    n: stats.n,
    dataSource: `Based on ${stats.n} PE-certified measurements`
  }
}

/**
 * Calculate ROI with real corpus statistics
 *
 * NOW INCLUDES 5-COMPONENT CORPUS-VALIDATED VALUE MODEL:
 * 1. Direct energy savings (116 PE-certified studies)
 * 2. Avoided future degradation (ASHRAE 1998 Handbook Ch 2.9)
 * 3. Avoided peak demand charges (Industry kW/ton metrics)
 * 4. Avoided extended runtime (50% capacity-to-runtime factor)
 * 5. Life extension value (20% per roi_calculator_logic.json)
 *
 * @param {Object} params - Input parameters
 * @returns {Object} ROI calculation with uncertainty
 */
export function calculateROI({ equipmentType, capacityTons, annualKwh, electricityRate, equipmentAge, operatingHours = 2500, demandRate = 12 }) {
  const improvement = calculateExpectedImprovement(equipmentType, equipmentAge)

  const treatmentCost = capacityTons * FINANCIAL_STATS.treatmentCostPerTon.value
  const annualEnergyCost = annualKwh * electricityRate

  // 1. Direct energy savings
  const expectedEnergySavings = annualEnergyCost * (parseFloat(improvement.expected) / 100)
  const floorSavings = annualEnergyCost * (parseFloat(improvement.floor) / 100)
  const optimisticSavings = annualEnergyCost * (parseFloat(improvement.optimistic) / 100)

  // Normalize equipment type for degradation functions
  const normalizedType = equipmentType?.includes('Split') ? 'Split'
    : equipmentType?.includes('CRAC') ? 'CRAC'
    : equipmentType?.includes('RTU') ? 'RTU'
    : 'Chiller';

  // 2. Avoided future degradation (ASHRAE 2-3%/year)
  const avoidedDegradation = calculateAvoidedDegradationCosts({
    equipmentType: normalizedType,
    currentAge: equipmentAge,
    tonnage: capacityTons,
    operatingHours,
    electricityRate,
    years: 10
  });

  // 3. Avoided peak demand charges
  const avoidedDemand = calculateAvoidedDemandCharges({
    equipmentType: normalizedType,
    currentAge: equipmentAge,
    tonnage: capacityTons,
    demandRate,
    years: 10
  });

  // 4. Avoided extended runtime (50% capacity-to-runtime factor)
  const avoidedRuntime = calculateAvoidedExtendedRuntimeCosts({
    equipmentType: normalizedType,
    currentAge: equipmentAge,
    tonnage: capacityTons,
    operatingHours,
    electricityRate,
    years: 10
  });

  // 5. Life extension value (20% per corpus)
  const lifeExtension = calculateLifeExtensionValue({
    equipmentType: normalizedType,
    currentAge: equipmentAge,
    tonnage: capacityTons,
    discountRate: 0.08
  });

  // Total annual value (5 corpus-validated components)
  const totalAnnualValue =
    expectedEnergySavings +
    avoidedDegradation.annualizedAvoidedCost +
    avoidedDemand.annualizedAvoidedDemand +
    avoidedRuntime.annualizedAvoidedRuntime +
    lifeExtension.annualizedLifeExtensionValue;

  // Total avoided costs (excluding direct energy savings)
  const totalAvoidedCosts =
    avoidedDegradation.annualizedAvoidedCost +
    avoidedDemand.annualizedAvoidedDemand +
    avoidedRuntime.annualizedAvoidedRuntime +
    lifeExtension.annualizedLifeExtensionValue;

  return {
    treatmentCost,
    improvement,

    // Direct energy savings (legacy, for backwards compatibility)
    annualSavings: {
      expected: Math.round(expectedEnergySavings),
      floor: Math.round(floorSavings),
      optimistic: Math.round(optimisticSavings)
    },

    // NEW: 5-component comprehensive value
    totalAnnualValue: Math.round(totalAnnualValue),
    totalAvoidedCosts: Math.round(totalAvoidedCosts),

    // NEW: Value breakdown for display
    valueBreakdown: {
      energySavings: Math.round(expectedEnergySavings),
      avoidedDegradation: Math.round(avoidedDegradation.annualizedAvoidedCost),
      avoidedDemand: Math.round(avoidedDemand.annualizedAvoidedDemand),
      avoidedRuntime: Math.round(avoidedRuntime.annualizedAvoidedRuntime),
      lifeExtension: Math.round(lifeExtension.annualizedLifeExtensionValue)
    },

    // Payback using total value (not just energy savings)
    paybackMonths: {
      expected: totalAnnualValue > 0 ? ((treatmentCost / totalAnnualValue) * 12).toFixed(1) : 'N/A',
      floor: floorSavings > 0 ? ((treatmentCost / floorSavings) * 12).toFixed(1) : 'N/A', // Uses energy-only for floor estimate
      energyOnly: expectedEnergySavings > 0 ? ((treatmentCost / expectedEnergySavings) * 12).toFixed(1) : 'N/A'
    },

    // ROI using total value
    roi5Year: totalAnnualValue > 0
      ? Math.round(((totalAnnualValue * 5 - treatmentCost) / treatmentCost) * 100)
      : 0,

    dataSource: improvement.dataSource,
    modelVersion: '5-component corpus-validated'
  }
}

/**
 * Format uncertainty for display (e.g., "25.2% ± 13.1%")
 */
export function formatWithUncertainty(mean, stdDev, unit = '%') {
  return `${mean}${unit} ± ${stdDev}${unit}`
}

/**
 * Format confidence interval for display
 */
export function formatCI(lower, upper, unit = '%') {
  return `${lower}${unit} - ${upper}${unit}`
}

export default {
  EFFICIENCY_STATS,
  KW_TON_STATS,
  COP_STATS,
  EQUIPMENT_STATS,
  FINANCIAL_STATS,
  LOWER_PERFORMERS,
  SCREENING_CRITERIA,
  DATA_QUALITY,
  getEquipmentStats,
  calculateExpectedImprovement,
  calculateROI,
  formatWithUncertainty,
  formatCI
}
